(function () {
  'use strict';

  /**
   * Parse all #OptionName_OptionValue segments from an alt text string.
   * Returns an array of { option, value } objects (both lowercased).
   * Example: "Diamond stud #Setting Type_3 Prong Martini #Metal_14K White Gold"
   *   => [{ option: "setting type", value: "3 prong martini" }, { option: "metal", value: "14k white gold" }]
   */
  function parseAltFilters(alt) {
    if (!alt || !alt.includes('#')) return [];
    var filters = [];
    var parts = alt.split('#');
    for (var i = 1; i < parts.length; i++) {
      var segment = parts[i].trim();
      var underscoreIdx = segment.indexOf('_');
      if (underscoreIdx === -1) continue;
      var optionName = segment.substring(0, underscoreIdx).trim().toLowerCase();
      var optionValue = segment.substring(underscoreIdx + 1).trim().toLowerCase();
      if (optionName && optionValue) {
        filters.push({ option: optionName, value: optionValue });
      }
    }
    return filters;
  }

  /**
   * Given a variant object (with option1, option2, option3 and the product's
   * option names embedded in data attributes on the gallery), determine whether
   * a media element should be visible.
   *
   * selectedOptions: array of { name (lowercase), value (lowercase) }
   * altFilters: array of { option (lowercase), value (lowercase) }
   *
   * Rules:
   *  - If altFilters is empty (no # in alt), always show.
   *  - Otherwise, show only if EVERY filter in altFilters matches one of the selectedOptions.
   */
  function shouldShowMedia(altFilters, selectedOptions) {
    if (altFilters.length === 0) return true;
    return altFilters.every(function (filter) {
      return selectedOptions.some(function (sel) {
        return sel.name === filter.option && sel.value === filter.value;
      });
    });
  }

  /**
   * Apply visibility filtering to a single product-gallery element.
   * selectedOptions: array of { name (lowercase), value (lowercase) }
   */
  function applyFilter(gallery, selectedOptions) {
    // --- Carousel media items ---
    var mediaItems = gallery.querySelectorAll('.product-gallery__media');
    var visiblePositions = [];

    mediaItems.forEach(function (item) {
      // Read alt from the nested img or video element's alt attribute,
      // or from a data-alt attribute we can't rely on, so we look at the
      // image tag inside the item.
      var img = item.querySelector('img');
      var alt = (img && img.getAttribute('alt')) || '';
      var altFilters = parseAltFilters(alt);
      var visible = shouldShowMedia(altFilters, selectedOptions);

      if (visible) {
        item.removeAttribute('hidden');
        var pos = item.getAttribute('data-media-position') || item.getAttribute('data-index');
        if (pos) visiblePositions.push(parseInt(pos, 10));
      } else {
        item.setAttribute('hidden', '');
      }
    });

    // --- Thumbnail sidebar buttons ---
    var thumbnails = gallery.querySelectorAll('.product-gallery__thumbnail');
    thumbnails.forEach(function (thumb) {
      var pos = parseInt(thumb.getAttribute('data-media-position') || '0', 10);
      // Find the corresponding media item by data-media-id
      var mediaId = thumb.getAttribute('data-media-id');
      var correspondingMedia = mediaId
        ? gallery.querySelector('.product-gallery__media[data-media-id="' + mediaId + '"]')
        : null;

      if (correspondingMedia) {
        if (correspondingMedia.hasAttribute('hidden')) {
          thumb.setAttribute('hidden', '');
        } else {
          thumb.removeAttribute('hidden');
        }
      }
    });

    // --- Dot navigation buttons ---
    var dots = gallery.querySelectorAll('.page-dots button');
    dots.forEach(function (dot, idx) {
      var correspondingMedia = mediaItems[idx];
      if (correspondingMedia) {
        if (correspondingMedia.hasAttribute('hidden')) {
          dot.setAttribute('hidden', '');
        } else {
          dot.removeAttribute('hidden');
        }
      }
    });

    // Scroll to the first visible media item if the current one is hidden
    var currentMedia = gallery.querySelector('.product-gallery__media[aria-current="true"], .product-gallery__media.is-selected');
    if (currentMedia && currentMedia.hasAttribute('hidden')) {
      var firstVisible = gallery.querySelector('.product-gallery__media:not([hidden])');
      if (firstVisible) {
        firstVisible.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  }

  /**
   * Extract selected options from a variant object.
   * The Prestige theme stores option names on the variant-picker element
   * via data attributes, or we can read them from the product JSON.
   * We use the variant's option1/option2/option3 values combined with
   * the option names read from the variant-picker fieldset legends.
   */
  function getSelectedOptions(gallery, variant) {
    if (!variant) return [];

    // Try to find the variant-picker to get option names
    var section = gallery.closest('[id^="shopify-section-"]');
    var variantPicker = section
      ? section.querySelector('variant-picker')
      : document.querySelector('variant-picker');

    var optionNames = [];
    if (variantPicker) {
      var legends = variantPicker.querySelectorAll('fieldset legend');
      legends.forEach(function (legend) {
        optionNames.push(legend.textContent.trim().replace(/:$/, '').trim().toLowerCase());
      });
    }

    var selectedOptions = [];
    var optionValues = [variant.option1, variant.option2, variant.option3];
    optionValues.forEach(function (val, idx) {
      if (val) {
        selectedOptions.push({
          name: optionNames[idx] || ('option' + (idx + 1)),
          value: val.toLowerCase()
        });
      }
    });

    return selectedOptions;
  }

  /**
   * Initialize filtering for a single product-gallery element on page load.
   */
  function initGallery(gallery) {
    // Read the initially selected variant from the Prestige theme's JSON script block
    var section = gallery.closest('[id^="shopify-section-"]');
    var variantScript = section
      ? section.querySelector('script[data-variant]')
      : document.querySelector('script[data-variant]');

    if (variantScript) {
      try {
        var variant = JSON.parse(variantScript.textContent);
        var selectedOptions = getSelectedOptions(gallery, variant);
        if (selectedOptions.length > 0) {
          applyFilter(gallery, selectedOptions);
        }
      } catch (e) {
        // JSON parse failed, skip initial filter
      }
    }

    // Listen for variant changes dispatched by the Prestige theme
    gallery.addEventListener('variant:changed', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      var selectedOptions = getSelectedOptions(gallery, variant);
      applyFilter(gallery, selectedOptions);
    });
  }

  /**
   * Main init: find all product-gallery elements and set up filtering.
   * Uses MutationObserver to handle dynamically added galleries (e.g. quick buy).
   */
  function init() {
    document.querySelectorAll('product-gallery').forEach(initGallery);

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName && node.tagName.toLowerCase() === 'product-gallery') {
            initGallery(node);
          }
          var nested = node.querySelectorAll && node.querySelectorAll('product-gallery');
          if (nested) nested.forEach(initGallery);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();